import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  GraduationCap, Home, Plus, TrendingUp, Trophy, Loader2, 
  Star, Target, AlertTriangle, CheckCircle, Award, Gamepad2,
  BookOpen, Calculator, Beaker, Globe, History, HelpCircle,
  ChevronDown, ChevronUp, Gift, Clock, Users, BarChart3
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Subject icons mapping
const subjectIcons = {
  math: Calculator,
  language: BookOpen,
  science: Beaker,
  geography: Globe,
  history: History,
  'true-false': HelpCircle,
  truefalse: HelpCircle,
  mixed: Star,
  abc: BookOpen,
  counting: Calculator,
  colors: Star,
  animals: Target,
  default: Gamepad2
};

const getSubjectIcon = (subject) => {
  const normalizedSubject = subject?.toLowerCase().replace(/[^a-z]/g, '') || 'default';
  return subjectIcons[normalizedSubject] || subjectIcons.default;
};

const formatSubjectName = (subject) => {
  if (!subject) return 'Unknown';
  return subject
    .replace(/-/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim();
};

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const [teacherId] = useState(localStorage.getItem('teacherId'));
  const [teacherName] = useState(localStorage.getItem('teacherName'));
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linkingStudent, setLinkingStudent] = useState(false);
  const [linkCode, setLinkCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedStudent, setExpandedStudent] = useState(null);

  useEffect(() => {
    if (!teacherId) {
      navigate('/teacher-setup');
      return;
    }
    fetchStudents();
  }, [teacherId, navigate]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/teacher/${teacherId}/students`);
      setStudents(response.data.students || []);
      // Auto-expand first student if only one
      if (response.data.students?.length === 1) {
        setExpandedStudent(response.data.students[0].player_id);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      setError('Failed to load students data');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkStudent = async () => {
    if (!linkCode.trim()) {
      setError('Please enter the 6-digit link code');
      return;
    }

    try {
      setLinkingStudent(true);
      setError('');
      const response = await axios.post(`${API}/teacher/${teacherId}/link-student`, {
        link_code: linkCode
      });
      setSuccess(`Successfully linked ${response.data.student_name}!`);
      setLinkCode('');
      setTimeout(() => {
        setSuccess('');
        fetchStudents();
      }, 2000);
    } catch (err) {
      console.error('Error linking student:', err);
      setError(err.response?.data?.detail || 'Failed to link student. Please check the code and try again.');
    } finally {
      setLinkingStudent(false);
    }
  };

  const toggleStudentExpand = (studentId) => {
    setExpandedStudent(expandedStudent === studentId ? null : studentId);
  };

  // Calculate class statistics
  const classStats = {
    totalStudents: students.length,
    totalGames: students.reduce((sum, s) => sum + s.games_played, 0),
    avgScore: students.length > 0 
      ? Math.round(students.reduce((sum, s) => sum + s.average_score, 0) / students.length) 
      : 0,
    totalPoints: students.reduce((sum, s) => sum + (s.points || 0), 0)
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{
        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
      }}>
        <Card className="p-12 bg-white/95 backdrop-blur-sm shadow-2xl text-center" style={{ borderRadius: '32px' }}>
          <Loader2 className="w-16 h-16 mx-auto mb-4 text-green-500 animate-spin" />
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Loading Classroom...</h2>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8" style={{
      background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
    }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-lg font-semibold text-white hover:scale-110 transition-transform"
          >
            <Home className="w-5 h-5 mr-2" />
            Home
          </Button>
        </div>

        <div className="text-center mb-8 bounce-in">
          <GraduationCap className="w-16 h-16 mx-auto mb-4 text-white" />
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">{teacherName}'s Classroom</h1>
          <p className="text-xl text-white opacity-90">Track Your Students' Learning Progress</p>
        </div>

        {/* Class Overview Stats */}
        {students.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="p-4 text-center bg-white/95" style={{ borderRadius: '16px' }}>
              <Users className="w-8 h-8 mx-auto mb-1 text-green-600" />
              <p className="text-2xl font-bold text-gray-800">{classStats.totalStudents}</p>
              <p className="text-xs text-gray-600">Students</p>
            </Card>
            <Card className="p-4 text-center bg-white/95" style={{ borderRadius: '16px' }}>
              <Gamepad2 className="w-8 h-8 mx-auto mb-1 text-blue-600" />
              <p className="text-2xl font-bold text-gray-800">{classStats.totalGames}</p>
              <p className="text-xs text-gray-600">Total Games</p>
            </Card>
            <Card className="p-4 text-center bg-white/95" style={{ borderRadius: '16px' }}>
              <BarChart3 className="w-8 h-8 mx-auto mb-1 text-purple-600" />
              <p className="text-2xl font-bold text-gray-800">{classStats.avgScore}</p>
              <p className="text-xs text-gray-600">Class Avg Score</p>
            </Card>
            <Card className="p-4 text-center bg-white/95" style={{ borderRadius: '16px' }}>
              <Star className="w-8 h-8 mx-auto mb-1 text-yellow-600" />
              <p className="text-2xl font-bold text-gray-800">{classStats.totalPoints}</p>
              <p className="text-xs text-gray-600">Total Points</p>
            </Card>
          </div>
        )}

        {/* Link Student Section */}
        <Card className="p-6 mb-8 bg-white/95" style={{ borderRadius: '24px' }}>
          <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
            <Plus className="w-6 h-6 mr-2 text-green-500" />
            Add a Student
          </h2>
          <p className="text-gray-600 mb-4">Enter your student's 6-digit link code (found in their profile)</p>
          <div className="flex gap-4">
            <Input
              type="text"
              placeholder="Enter 6-digit code..."
              value={linkCode}
              onChange={(e) => setLinkCode(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleLinkStudent()}
              className="text-lg p-4 border-2 border-green-300 font-mono tracking-widest"
              style={{ borderRadius: '12px' }}
              maxLength={6}
            />
            <Button
              onClick={handleLinkStudent}
              disabled={linkingStudent}
              className="px-8 py-4 text-lg font-bold"
              style={{
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                borderRadius: '12px'
              }}
            >
              {linkingStudent ? 'Adding...' : 'Add Student'}
            </Button>
          </div>
          {error && <p className="text-red-600 mt-2">{error}</p>}
          {success && <p className="text-green-600 mt-2 font-semibold">{success}</p>}
        </Card>

        {/* Students Progress Cards */}
        {students.length === 0 ? (
          <Card className="p-12 text-center bg-white/95" style={{ borderRadius: '24px' }}>
            <GraduationCap className="w-20 h-20 mx-auto mb-4 text-gray-400" />
            <h3 className="text-2xl font-bold text-gray-600 mb-2">No Students Yet</h3>
            <p className="text-gray-500">Add students using their link codes to start tracking their progress</p>
          </Card>
        ) : (
          <div className="space-y-6">
            {students.map((student) => (
              <Card key={student.player_id} className="bg-white/95 overflow-hidden slide-up" style={{ borderRadius: '24px' }}>
                {/* Student Header - Always Visible */}
                <div 
                  className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleStudentExpand(student.player_id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="text-5xl">{student.avatar || '🧒'}</div>
                      <div>
                        <h3 className="text-3xl font-bold text-gray-800">{student.player_name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                            student.subscription_status === 'active' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {student.subscription_status === 'active' ? '✓ Active' : '✗ Inactive'}
                          </span>
                          <span className="text-gray-500">•</span>
                          <span className="text-gray-600">{student.games_played} games played</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      {/* Quick Stats */}
                      <div className="hidden md:flex items-center gap-4">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-yellow-600">{student.points || 0}</p>
                          <p className="text-xs text-gray-500">Points</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-green-600">{student.total_score}</p>
                          <p className="text-xs text-gray-500">Score</p>
                        </div>
                      </div>
                      {expandedStudent === student.player_id ? (
                        <ChevronUp className="w-8 h-8 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-8 h-8 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedStudent === student.player_id && (
                  <div className="border-t border-gray-200 p-6 bg-gray-50">
                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                      <Card className="p-4 text-center bg-gradient-to-br from-yellow-100 to-orange-100" style={{ borderRadius: '16px' }}>
                        <Trophy className="w-8 h-8 mx-auto mb-1 text-yellow-600" />
                        <p className="text-2xl font-bold text-yellow-700">{student.total_score}</p>
                        <p className="text-xs text-gray-600">Total Score</p>
                      </Card>
                      <Card className="p-4 text-center bg-gradient-to-br from-blue-100 to-cyan-100" style={{ borderRadius: '16px' }}>
                        <Gamepad2 className="w-8 h-8 mx-auto mb-1 text-blue-600" />
                        <p className="text-2xl font-bold text-blue-700">{student.games_played}</p>
                        <p className="text-xs text-gray-600">Games Played</p>
                      </Card>
                      <Card className="p-4 text-center bg-gradient-to-br from-green-100 to-emerald-100" style={{ borderRadius: '16px' }}>
                        <TrendingUp className="w-8 h-8 mx-auto mb-1 text-green-600" />
                        <p className="text-2xl font-bold text-green-700">{student.average_score}</p>
                        <p className="text-xs text-gray-600">Avg Score</p>
                      </Card>
                      <Card className="p-4 text-center bg-gradient-to-br from-purple-100 to-pink-100" style={{ borderRadius: '16px' }}>
                        <Star className="w-8 h-8 mx-auto mb-1 text-purple-600" />
                        <p className="text-2xl font-bold text-purple-700">{student.points || 0}</p>
                        <p className="text-xs text-gray-600">Reward Points</p>
                      </Card>
                      <Card className="p-4 text-center bg-gradient-to-br from-pink-100 to-rose-100" style={{ borderRadius: '16px' }}>
                        <Gift className="w-8 h-8 mx-auto mb-1 text-pink-600" />
                        <p className="text-2xl font-bold text-pink-700">{student.prizes_owned || 0}</p>
                        <p className="text-xs text-gray-600">Prizes Owned</p>
                      </Card>
                    </div>

                    {/* Strengths & Improvements Section */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                      {/* Excelling In */}
                      <Card className="p-5 bg-white border-2 border-green-200" style={{ borderRadius: '16px' }}>
                        <h4 className="text-lg font-bold text-green-700 mb-4 flex items-center">
                          <CheckCircle className="w-6 h-6 mr-2" />
                          Excelling In 🌟
                        </h4>
                        {student.strengths && student.strengths.length > 0 ? (
                          <div className="space-y-3">
                            {student.strengths.map((strength, idx) => {
                              const IconComponent = getSubjectIcon(strength.subject);
                              return (
                                <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-xl">
                                  <div className="flex items-center gap-3">
                                    <div className="p-2 bg-green-200 rounded-lg">
                                      <IconComponent className="w-5 h-5 text-green-700" />
                                    </div>
                                    <div>
                                      <p className="font-semibold text-gray-800">{formatSubjectName(strength.subject)}</p>
                                      <p className="text-xs text-gray-500">{strength.games_played} games • {strength.correct_answers}/{strength.total_questions} correct</p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-xl font-bold text-green-600">{strength.accuracy}%</p>
                                    <p className="text-xs text-gray-500">accuracy</p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-6 text-gray-500">
                            <Star className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                            <p>More games needed to identify strengths</p>
                          </div>
                        )}
                      </Card>

                      {/* Needs Improvement */}
                      <Card className="p-5 bg-white border-2 border-amber-200" style={{ borderRadius: '16px' }}>
                        <h4 className="text-lg font-bold text-amber-700 mb-4 flex items-center">
                          <AlertTriangle className="w-6 h-6 mr-2" />
                          Needs Practice 💪
                        </h4>
                        {student.needs_improvement && student.needs_improvement.length > 0 ? (
                          <div className="space-y-3">
                            {student.needs_improvement.map((area, idx) => {
                              const IconComponent = getSubjectIcon(area.subject);
                              return (
                                <div key={idx} className="flex items-center justify-between p-3 bg-amber-50 rounded-xl">
                                  <div className="flex items-center gap-3">
                                    <div className="p-2 bg-amber-200 rounded-lg">
                                      <IconComponent className="w-5 h-5 text-amber-700" />
                                    </div>
                                    <div>
                                      <p className="font-semibold text-gray-800">{formatSubjectName(area.subject)}</p>
                                      <p className="text-xs text-gray-500">{area.games_played} games • {area.correct_answers}/{area.total_questions} correct</p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-xl font-bold text-amber-600">{area.accuracy}%</p>
                                    <p className="text-xs text-gray-500">accuracy</p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-6 text-gray-500">
                            <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-300" />
                            <p>Great job! No areas need immediate attention.</p>
                          </div>
                        )}
                      </Card>
                    </div>

                    {/* Subject Performance Breakdown */}
                    {student.subject_performance && student.subject_performance.length > 0 && (
                      <Card className="p-5 bg-white mb-6" style={{ borderRadius: '16px' }}>
                        <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                          <Award className="w-6 h-6 mr-2 text-green-500" />
                          Subject Performance Breakdown
                        </h4>
                        <div className="space-y-4">
                          {student.subject_performance.map((perf, idx) => {
                            const IconComponent = getSubjectIcon(perf.subject);
                            const accuracyColor = perf.accuracy >= 70 ? 'bg-green-500' : perf.accuracy >= 50 ? 'bg-yellow-500' : 'bg-red-400';
                            return (
                              <div key={idx} className="p-3 bg-gray-50 rounded-xl">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-3">
                                    <IconComponent className="w-5 h-5 text-gray-600" />
                                    <span className="font-semibold text-gray-800">{formatSubjectName(perf.subject)}</span>
                                  </div>
                                  <div className="flex items-center gap-4 text-sm">
                                    <span className="text-gray-500">{perf.games_played} games</span>
                                    <span className="font-bold text-green-600">{perf.accuracy}%</span>
                                  </div>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div 
                                    className={`h-2 rounded-full ${accuracyColor} transition-all duration-500`}
                                    style={{ width: `${perf.accuracy}%` }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </Card>
                    )}

                    {/* Recent Activity */}
                    {student.recent_sessions && student.recent_sessions.length > 0 && (
                      <Card className="p-5 bg-white" style={{ borderRadius: '16px' }}>
                        <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                          <Clock className="w-6 h-6 mr-2 text-purple-500" />
                          Recent Activity
                        </h4>
                        <div className="space-y-3">
                          {student.recent_sessions.map((session, idx) => {
                            const accuracy = session.total_questions > 0 
                              ? Math.round((session.correct_answers / session.total_questions) * 100) 
                              : 0;
                            return (
                              <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                                <div className="flex items-center gap-3">
                                  <div className={`p-2 rounded-lg ${accuracy >= 70 ? 'bg-green-100' : accuracy >= 50 ? 'bg-yellow-100' : 'bg-red-100'}`}>
                                    <Gamepad2 className={`w-5 h-5 ${accuracy >= 70 ? 'text-green-600' : accuracy >= 50 ? 'text-yellow-600' : 'text-red-500'}`} />
                                  </div>
                                  <div>
                                    <p className="font-semibold text-gray-800 capitalize">
                                      {session.game_format} - {formatSubjectName(session.subject)}
                                    </p>
                                    <p className="text-xs text-gray-500 capitalize">{session.age_group}</p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-lg font-bold text-green-600">{session.score} pts</p>
                                  <p className="text-xs text-gray-500">{session.correct_answers}/{session.total_questions} ({accuracy}%)</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </Card>
                    )}

                    {/* No Activity Message */}
                    {(!student.recent_sessions || student.recent_sessions.length === 0) && (
                      <Card className="p-8 text-center bg-white" style={{ borderRadius: '16px' }}>
                        <Gamepad2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <h4 className="text-xl font-bold text-gray-600 mb-2">No Activity Yet</h4>
                        <p className="text-gray-500">Once {student.player_name} starts playing, their progress will appear here!</p>
                      </Card>
                    )}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherDashboard;
